/**
 * Indikátor čerstvosti dat (spec 08 §5).
 *
 * „Nenápadný, ale vždy viditelný" — v hlavičce sedí jako tečka s časem.
 * Když data zastarají, eskaluje na žlutý banner, protože to už je informace,
 * která mění výklad všech čísel na obrazovce.
 *
 * Pozn.: `/etl/health` má pole pojmenovaná česky (`stav`, `lag_hodin`, `joby`) —
 * je to backend kontrakt, ne překlep.
 */
import { useEtlHealth } from '@/api/queries';
import { formatAge } from '@/lib/formatCz';

/** Backend posílá `stav` jako volný string; „ok" je jediný bezproblémový. */
function isHealthy(stav: string): boolean {
  return stav.toLowerCase() === 'ok';
}

/** Porucha je jen job, jehož poslední běh selhal. „běží", „vypnuto" ani „nikdy nespuštěn"
 *  (např. týdenní AI job před prvním během) porucha není — čerstvost dat hlídá lag. */
const isFailed = (stav: string) => stav.toLowerCase() === 'selhal';

export function EtlFreshnessIndicator() {
  const { data, isLoading, isError } = useEtlHealth();

  if (isLoading) {
    return <span className="etl-dot etl-dot--loading" title="Zjišťuji čerstvost dat…" />;
  }
  if (isError || !data) {
    return (
      <span className="etl-dot etl-dot--unknown" title="Čerstvost dat se nepodařilo zjistit">
        <span className="etl-dot__mark" aria-hidden="true" />
        data: neznámo
      </span>
    );
  }

  const failedJobs = data.joby.filter((job) => isFailed(job.stav));
  const tone = isHealthy(data.stav) && failedJobs.length === 0 ? 'fresh' : 'stale';

  return (
    <span
      className={`etl-dot etl-dot--${tone}`}
      title={
        `${data.hlaska}\n` +
        `Zpoždění: ${formatAge(data.lag_hodin)} (práh ${data.lag_prah_hodin} h)\n` +
        `Režim: ${data.rezim}` +
        (failedJobs.length > 0
          ? `\nProblémové joby: ${failedJobs.map((j) => j.job).join(', ')}`
          : '')
      }
    >
      <span className="etl-dot__mark" aria-hidden="true" />
      data {formatAge(data.lag_hodin)}
    </span>
  );
}

/**
 * Žlutý banner pod hlavičkou. Renderuje se jen když je opravdu co říct —
 * banner, který visí pořád, přestane být vidět.
 */
export function EtlStaleBanner() {
  const { data } = useEtlHealth();
  if (!data) return null;

  const failedJobs = data.joby.filter((job) => isFailed(job.stav));
  if (isHealthy(data.stav) && failedJobs.length === 0) return null;

  return (
    <div className="banner banner--warning" role="status">
      <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
        <path
          d="M8 1.8 15 14H1L8 1.8Z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
        <path d="M8 6.2v3.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        <circle cx="8" cy="11.8" r="0.75" fill="currentColor" />
      </svg>
      <span>
        <strong>{data.hlaska}</strong>{' '}
        {!isHealthy(data.stav) ? (
          <>
            Zpoždění dat je {formatAge(data.lag_hodin)}, práh je {data.lag_prah_hodin} h —
            čísla níže nemusí zahrnovat poslední období.
          </>
        ) : null}
        {failedJobs.length > 0 ? (
          <>
            {' '}
            Problémové joby: {failedJobs.map((j) => j.job).join(', ')}. Zobrazená data
            pocházejí z posledního úspěšného běhu.
          </>
        ) : null}
      </span>
    </div>
  );
}
